import pageStyles from "../SettingsPage.module.css";
import { useOptionalAppConfig } from "../../services/studio/AppConfigProvider";
import { backendModuleSubtitle, findFrontendTileForBackendModule } from "../../skills/backendModuleTile";
import { useUserModulePrefs } from "../../shared/userSkillPrefs";
import type { Channel } from "../../shared/host";

/**
 * Per-module toggles for the signed-in user (ST-04), rendered from the
 * `moduleCatalogue` the settings endpoint returns
 * (`GET /api/{channel}/settings`) — no extra request.
 *
 * Reads `moduleCatalogue` rather than `modules` on purpose. `modules` is the
 * enabled subset that drives the launcher and the tab bar, where a disabled
 * module must vanish entirely; this screen is the one place a module switched
 * off in Studio should still appear, so the user hunting for it gets an answer
 * instead of silence. Modules blocked by the other gates — retired from the
 * catalogue, not routed to this channel, not assigned to the persona — are
 * absent from both lists, because those mean "not yours" rather than
 * "temporarily off".
 *
 * Two layers decide a row's effective state, and the workspace flag is the
 * ceiling (ST-06 — the user hides within the configured set, never surfaces):
 *   - `module.enabled` (StudioModule.Enabled, set on the Studio Modules page)
 *     — when false the row renders present-and-off and is NOT user-toggleable.
 *   - the user's own disable list (useUserModulePrefs, localStorage per host,
 *     keyed by the frontend tile id) — toggleable here and honoured by
 *     TaskPane's launcher/tab-bar assembly, so a module the user switches off
 *     is "removed from the launcher and tab bar entirely" (ST-04 AC).
 *
 * Rows that resolve to no frontend tile stay read-only: they render no
 * launcher surface, so there is nothing a toggle could hide — and no stable
 * id to key the preference on.
 */

interface ModulesTabProps {
  readonly host: Channel | null;
}

export function ModulesTab({ host }: Readonly<ModulesTabProps>) {
  const appConfig = useOptionalAppConfig();
  const data = appConfig?.data ?? null;
  const status = appConfig?.status ?? "idle";
  const { isModuleDisabled, toggleModule } = useUserModulePrefs(host);

  if (status === "loading" || status === "idle") {
    return (
      <div className={pageStyles.section}>
        <h3 className={pageStyles.sectionTitle}>Modules</h3>
        <p className={pageStyles.placeholder}>Loading…</p>
      </div>
    );
  }

  if (status === "error" || !data) {
    return (
      <div className={pageStyles.section}>
        <h3 className={pageStyles.sectionTitle}>Modules</h3>
        <p className={pageStyles.placeholder}>
          Could not load modules. Pull-to-refresh or try again later.
        </p>
      </div>
    );
  }

  const modules = data.moduleCatalogue;
  if (modules.length === 0) {
    return (
      <div className={pageStyles.section}>
        <h3 className={pageStyles.sectionTitle}>Modules</h3>
        <div className={pageStyles.infoBanner}>
          No modules are available for your persona in this channel.
        </div>
      </div>
    );
  }

  return (
    <div className={pageStyles.section}>
      <h3 className={pageStyles.sectionTitle}>Modules</h3>
      <div className={pageStyles.infoBanner}>
        Your administrator decides which modules are available. Switch one off
        here to remove it from your launcher and tab bar — only you are
        affected, and you can switch it back on any time.
      </div>

      {modules.map((m) => {
        // Same wording the launcher tile carries ("PDF → Word", "Slide Design"),
        // resolved through the shared tile mapping so the two surfaces can't
        // describe the same module differently. Empty for a catalogue module the
        // add-in has no tile for — the row then shows the name alone.
        const subtitle = backendModuleSubtitle(m);
        // The launcher/tab-bar identity this row controls. Null when the
        // catalogue module has no add-in tile — the row is then read-only.
        const tileId = findFrontendTileForBackendModule(m)?.id ?? null;
        const userTogglable = m.enabled && tileId !== null;
        const effectivelyOn = m.enabled && !(tileId !== null && isModuleDisabled(tileId));
        return (
          <div key={m.id} className={pageStyles.row}>
            <div className={pageStyles.rowInfo}>
              <div className={pageStyles.rowLabel}>{m.name}</div>
              <div className={pageStyles.rowDesc}>
                {m.enabled ? subtitle : [subtitle, "turned off by your administrator"].filter(Boolean).join(" · ")}
              </div>
            </div>
            <label className={pageStyles.toggle}>
              <input
                type="checkbox"
                role="switch"
                aria-label={`${m.name} module status`}
                checked={effectivelyOn}
                // Workspace-disabled (admin ceiling) and tile-less rows stay
                // locked: the first is the administrator's decision, the second
                // has no launcher surface a toggle could hide.
                disabled={!userTogglable}
                onChange={userTogglable && tileId ? () => toggleModule(tileId) : undefined}
              />
              <span className={pageStyles.toggleSlider} />
            </label>
          </div>
        );
      })}
    </div>
  );
}
